import { createFileRoute } from '@tanstack/react-router';
import { privateCacheHeaders } from '@/analytics/cache-headers';
import { useEffect, useState } from 'react';
import { Schema } from 'effect';
import { authClient } from '@/lib/auth-client';

const searchSchema = Schema.Struct({
  extensionId: Schema.String,
});

export const Route = createFileRoute('/_marketing/browserExtAuthCallback')({
  // Auth surface — never shared-cached, whatever the layout default is.
  headers: privateCacheHeaders(),
  validateSearch: Schema.toStandardSchemaV1(searchSchema),
  head: () => ({
    meta: [{ name: 'robots', content: 'noindex, nofollow' }],
  }),
  component: Page,
});

type Status =
  | { kind: 'loading' }
  | { kind: 'success' }
  | { kind: 'error'; message: string };

const SEND_MESSAGE_TIMEOUT_MS = 10_000;

function Page() {
  const { extensionId } = Route.useSearch();
  const [status, setStatus] = useState<Status>({ kind: 'loading' });

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const { data } = await authClient.getSession();
        const token = data?.session?.token;
        const user = data?.user;

        const message =
          token && user?.id && user.email
            ? {
                kind: 'better-auth',
                token,
                user: { id: user.id, email: user.email },
              }
            : { kind: 'error', message: 'Failed to get session after OAuth' };

        // oxlint-disable-next-line typescript-eslint/no-explicit-any, typescript-eslint/no-unsafe-member-access -- chrome.runtime is not typed on window
        const runtime = (window as any).chrome?.runtime as
          | {
              sendMessage?: (
                id: string,
                payload: unknown,
                cb?: () => void,
              ) => void;
              lastError?: { message?: string };
            }
          | undefined;

        if (!runtime?.sendMessage) {
          throw new Error(
            'Extension messaging is unavailable on this page. Check that the extension is installed and externally_connectable includes this origin.',
          );
        }

        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(
              new Error(
                'Extension did not respond within 10 seconds. Is the extension installed?',
              ),
            );
          }, SEND_MESSAGE_TIMEOUT_MS);

          runtime.sendMessage?.(extensionId, message, () => {
            clearTimeout(timeout);
            const lastErrorMessage = runtime.lastError?.message;
            if (lastErrorMessage) {
              reject(new Error(lastErrorMessage));
              return;
            }
            resolve();
          });
        });

        if (!controller.signal.aborted) {
          setStatus({ kind: 'success' });
          window.close();
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setStatus({
            kind: 'error',
            message: error instanceof Error ? error.message : String(error),
          });
        }
      }
    })();

    return () => controller.abort();
  }, [extensionId]);

  if (status.kind === 'error') {
    return (
      <p className='p-4 text-red-600'>
        Could not complete extension sign-in: {status.message}
      </p>
    );
  }

  if (status.kind === 'success') {
    return <p className='p-4'>Sign-in complete. You can close this tab.</p>;
  }

  return <p className='p-4'>Completing sign-in…</p>;
}
